import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";
import { readConsent, writeConsent } from "@/components/CookieBanner";

const COOKIES = [
    {
        name: "Essential",
        purpose: "Keep the site working. Cart, session, security, fraud prevention.",
        examples: "uknitwear_session, csrf_token, mnfst_cart (localStorage)",
        duration: "Session / 30 days",
        optional: false,
    },
    {
        name: "Functional",
        purpose: "Remember your preferences (region, currency, wishlist).",
        examples: "mnfst_wishlist, preferred_currency",
        duration: "1 year",
        optional: true,
    },
    {
        name: "Analytics",
        purpose: "Understand how the site is used so we can improve it.",
        examples: "PostHog (_ph), Plausible (anonymised IP)",
        duration: "26 months",
        optional: true,
    },
    {
        name: "Marketing",
        purpose: "Measure ad performance and retarget on social platforms.",
        examples: "Meta Pixel (_fbp), Google Ads",
        duration: "180 days",
        optional: true,
    },
];

const Cookies = () => {
    const stored = readConsent();
    const [prefs, setPrefs] = useState({
        functional: stored?.functional ?? true,
        analytics: stored?.analytics ?? true,
        marketing: stored?.marketing ?? false,
    });

    const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

    const save = () => {
        writeConsent(prefs);
        toast.success("Cookie preferences saved.");
    };

    const acceptAll = () => {
        const next = { functional: true, analytics: true, marketing: true };
        setPrefs(next);
        writeConsent(next);
        toast.success("All cookies enabled.");
    };

    const rejectAll = () => {
        const next = { functional: false, analytics: false, marketing: false };
        setPrefs(next);
        writeConsent(next);
        toast.success("Optional cookies disabled.");
    };

    return (
        <PageShell
            kicker="[Legal / Last Updated 01.02.2026]"
            title="Cookies."
            intro="A cookie is a small file we (or our partners) store on your device. Some are essential to make the site work. Others help us improve it. You're in control of the optional ones."
            testid="cookies-page"
        >
            <section className="px-4 md:px-8 py-10 border-b-4 border-ink">
                <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    // 01 / What We Use
                </span>
                <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1 mb-6">
                    Cookie Inventory
                </h2>
                <div className="border-2 border-ink overflow-x-auto">
                    <table className="w-full font-mono-ui text-xs">
                        <thead className="bg-ink text-black uppercase tracking-[0.2em]">
                            <tr>
                                <th className="text-left p-3 font-mono-ui text-[10px]">Type</th>
                                <th className="text-left p-3 font-mono-ui text-[10px]">Purpose</th>
                                <th className="text-left p-3 font-mono-ui text-[10px] hidden md:table-cell">Examples</th>
                                <th className="text-left p-3 font-mono-ui text-[10px]">Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {COOKIES.map((c, i) => (
                                <tr
                                    key={c.name}
                                    className={i % 2 === 0 ? "bg-bone" : "bg-ink"}
                                    data-testid={`cookie-row-${i}`}
                                >
                                    <td className="p-3 border-t border-dotted border-ink/45">
                                        <strong>{c.name}</strong>
                                        {!c.optional && (
                                            <span className="ml-2 bg-hazard text-black px-1.5 py-0.5 text-[9px]">
                                                REQUIRED
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 border-t border-dotted border-ink/45">{c.purpose}</td>
                                    <td className="p-3 border-t border-dotted border-ink/45 hidden md:table-cell text-muted-foreground">
                                        {c.examples}
                                    </td>
                                    <td className="p-3 border-t border-dotted border-ink/45">{c.duration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="px-4 md:px-8 py-10 border-b-4 border-ink bg-bone">
                <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    // 02 / Your Choices
                </span>
                <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1 mb-6">
                    Preferences
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <PrefRow
                        label="Essential"
                        sub="Required for the site to function."
                        locked
                    />
                    <PrefRow
                        label="Functional"
                        sub="Wishlist, region, currency memory."
                        checked={prefs.functional}
                        onChange={() => toggle("functional")}
                        testid="cookie-pref-functional"
                    />
                    <PrefRow
                        label="Analytics"
                        sub="Anonymised usage data."
                        checked={prefs.analytics}
                        onChange={() => toggle("analytics")}
                        testid="cookie-pref-analytics"
                    />
                    <PrefRow
                        label="Marketing"
                        sub="Ad targeting & retargeting."
                        checked={prefs.marketing}
                        onChange={() => toggle("marketing")}
                        testid="cookie-pref-marketing"
                    />
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={save}
                        className="bg-ink text-black border-2 border-ink px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-hazard hover:border-hazard"
                        data-testid="cookies-save-button"
                    >
                        Save Preferences
                    </button>
                    <button
                        type="button"
                        onClick={acceptAll}
                        className="border-2 border-ink px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-ink hover:text-black"
                        data-testid="cookies-accept-all-button"
                    >
                        Accept All
                    </button>
                    <button
                        type="button"
                        onClick={rejectAll}
                        className="border-2 border-ink px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-ink hover:text-black"
                        data-testid="cookies-reject-all-button"
                    >
                        Reject Optional
                    </button>
                </div>
            </section>

            <section className="px-4 md:px-8 py-10 max-w-3xl">
                <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    // 03 / Browser Controls
                </span>
                <h2 className="font-heading text-3xl md:text-4xl uppercase leading-none mt-1">
                    You Can Also Block Them
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground/80">
                    Every modern browser lets you clear or block cookies directly. Note
                    that blocking essential cookies will break parts of the site (cart,
                    checkout, account).
                </p>
                <ul className="mt-4 space-y-1 font-mono-ui text-xs">
                    <li>Chrome — Settings → Privacy & Security → Cookies</li>
                    <li>Safari — Preferences → Privacy</li>
                    <li>Firefox — Settings → Privacy & Security</li>
                    <li>Edge — Settings → Cookies & Site Permissions</li>
                </ul>
            </section>
        </PageShell>
    );
};

const PrefRow = ({ label, sub, checked, onChange, locked, testid }) => (
    <label
        className={`flex items-center justify-between gap-4 border-2 border-ink p-4 bg-ink ${
            locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:bg-ink hover:text-black"
        }`}
    >
        <div>
            <h4 className="font-heading text-xl uppercase leading-none">{label}</h4>
            <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em]">{sub}</p>
        </div>
        <input
            type="checkbox"
            checked={locked ? true : checked}
            disabled={locked}
            onChange={onChange}
            className="w-5 h-5 accent-hazard"
            data-testid={testid}
        />
    </label>
);

export default Cookies;
